import { Dispatch, SetStateAction } from "react";
import {
  CategoryOutlined,
  ChecklistOutlined,
  DescriptionOutlined,
  FormatQuoteOutlined,
  InsertDriveFileOutlined,
  StorageOutlined,
  TimelineOutlined,
} from "@mui/icons-material";
import ArticleIcon from "@mui/icons-material/Article";
import { useUIStore } from "src/views/assistant/stores/UIStore";
import SourcesTab from "src/reusable/Sidebar/tabs/SourcesTab/SourcesTab";
import ArtifactsTab from "src/reusable/Sidebar/tabs/ArtifactsTab/ArtifactsTab";
import StagesTab from "../components/sidebar/StagesTab";
import InputDocumentsTab from "../components/sidebar/InputDocumentsTab";
import ReferenceDatasetsTab from "../components/sidebar/ReferenceDatasetsTab";
import ReportTemplateTab from "../components/sidebar/ReportTemplateTab";
import PlanTab from "../components/sidebar/PlanTab";
import ProposalGenerationReportPanel from "../components/ProposalGenerationReportPanel";
import SourcesDownloadButton from "src/views/assistant/components/sources/SourcesDownloadButton";
import SourcesViewToggle from "src/views/assistant/components/sources/SourcesViewToggle";
import AutoOpenToggle from "src/reusable/Sidebar/AutoOpenToggle";
import { SidebarConfig, SidebarTabConfig } from "src/reusable/Sidebar/types";
import {
  ProposalGenerationPlan,
  StageResponse,
} from "../services/ProposalGenerationServices";
import type { RfpFileState } from "../store/ProposalGenerationStore";
import type { Dataset } from "src/views/assistant/stores/ChatStore";

interface ProposalGenerationSidebarContext {
  responseSources: any[];
  artifacts: Record<string, any>;
  viewMode: "dataChunk" | "cardList";
  setViewMode: Dispatch<SetStateAction<"dataChunk" | "cardList">>;
  stages: StageResponse[];
  currentDialogueId: number;
  onStageClick: (dialogueId: number) => void;
  onClearStage?: (dialogueId: number) => void;
  onRemoveStage?: (dialogueId: number) => void;
  onAddStage?: (reportType: string) => void;
  isMutating?: boolean;
  inputDocuments: RfpFileState[];
  referenceDatasets: Dataset[];
  templateId: number | null;
  reportSdrId: number | null;
  hasGenerateReportBeenCalled: boolean;
  dialogueId: number | undefined;
  onIdentifyStages?: () => void;
  isIdentifyingStages?: boolean;
  plans: ProposalGenerationPlan[];
}

export const useProposalGenerationSidebar = (
  context: ProposalGenerationSidebarContext,
): SidebarConfig<ProposalGenerationSidebarContext> => {
  const { preferences } = useUIStore();

  return {
    tabs: [
      {
        id: "sources",
        label: "Sources",
        icon: FormatQuoteOutlined,
        renderContent: ({ context }) => {
          if (!context) return null;
          return (
            <SourcesTab
              sources={context.responseSources}
              viewMode={context.viewMode}
              setViewMode={context.setViewMode}
              // Three-tier provenance funnel (#20423 Story 4): without the
              // dialogue id SourcesTab silently falls back to the legacy list.
              dialogueId={context.dialogueId ?? null}
              detailSourceViewSdrId={null}
              detailSourceViewSourceId={null}
              setDetailSourceViewSdrId={() => {}}
              setDetailSourceViewSourceId={() => {}}
            />
          );
        },
        renderHeaderActions: ({ context }) => {
          if (!context) return null;
          return (
            <>
              <SourcesDownloadButton
                responseSources={context.responseSources}
              />
              <SourcesViewToggle
                alignment={context.viewMode}
                handleAlignment={(_, newValue) => {
                  if (newValue) {
                    context.setViewMode(newValue as "dataChunk" | "cardList");
                  }
                }}
              />
              <AutoOpenToggle />
            </>
          );
        },
        getBadgeContent: (context) => {
          return context?.responseSources.length ?? 0;
        },
      },
      {
        id: "artifacts",
        label: "Artifacts",
        icon: CategoryOutlined,
        renderContent: ({ context }) => {
          if (!context) return null;
          return <ArtifactsTab artifacts={context.artifacts} />;
        },
        renderHeaderActions: () => <AutoOpenToggle />,
        getBadgeContent: (context) => {
          return context?.artifacts ? Object.keys(context.artifacts).length : 0;
        },
      },
      ...(context.reportSdrId !== null || context.hasGenerateReportBeenCalled
        ? ([
            {
              id: "report",
              label: "Generated Report",
              icon: ArticleIcon,
              renderContent: ({
                context,
              }: {
                context?: ProposalGenerationSidebarContext;
              }) => {
                if (!context) return null;
                return (
                  <ProposalGenerationReportPanel
                    dialogueId={context.dialogueId}
                    onIdentifyStages={context.onIdentifyStages}
                    isIdentifyingStages={context.isIdentifyingStages}
                  />
                );
              },
              renderHeaderActions: () => null,
              getBadgeContent: (context?: ProposalGenerationSidebarContext) => {
                return context?.reportSdrId ? 1 : 0;
              },
            },
          ] as SidebarTabConfig<ProposalGenerationSidebarContext>[])
        : []),
      ...(context.plans.length > 0
        ? ([
            {
              id: "plan",
              label: "Plan",
              icon: ChecklistOutlined,
              renderContent: ({
                context,
              }: {
                context?: ProposalGenerationSidebarContext;
              }) => {
                if (!context) return null;
                return <PlanTab plans={context.plans} />;
              },
              renderHeaderActions: () => null,
              getBadgeContent: (context?: ProposalGenerationSidebarContext) => {
                return context?.plans.length ?? 0;
              },
            },
          ] as SidebarTabConfig<ProposalGenerationSidebarContext>[])
        : []),
      ...(context.inputDocuments.length > 0
        ? ([
            {
              id: "input-documents",
              label: "Input Documents",
              icon: InsertDriveFileOutlined,
              renderContent: ({
                context,
              }: {
                context?: ProposalGenerationSidebarContext;
              }) => {
                if (!context) return null;
                return (
                  <InputDocumentsTab inputDocuments={context.inputDocuments} />
                );
              },
              renderHeaderActions: () => null,
              getBadgeContent: (context?: ProposalGenerationSidebarContext) => {
                return context?.inputDocuments.length ?? 0;
              },
            },
          ] as SidebarTabConfig<ProposalGenerationSidebarContext>[])
        : []),
      ...(context.templateId
        ? ([
            {
              id: "report-template",
              label: "Report Template",
              icon: DescriptionOutlined,
              renderContent: ({
                context,
              }: {
                context?: ProposalGenerationSidebarContext;
              }) => {
                if (!context) return null;
                return <ReportTemplateTab templateId={context.templateId} />;
              },
              renderHeaderActions: () => null,
              getBadgeContent: (context?: ProposalGenerationSidebarContext) => {
                return context?.templateId ? 1 : 0;
              },
            },
          ] as SidebarTabConfig<ProposalGenerationSidebarContext>[])
        : []),
      ...(context.referenceDatasets.length > 0
        ? ([
            {
              id: "reference-datasets",
              label: "Reference Datasets",
              icon: StorageOutlined,
              renderContent: ({
                context,
              }: {
                context?: ProposalGenerationSidebarContext;
              }) => {
                if (!context) return null;
                return (
                  <ReferenceDatasetsTab datasets={context.referenceDatasets} />
                );
              },
              renderHeaderActions: () => null,
              getBadgeContent: (context?: ProposalGenerationSidebarContext) => {
                return context?.referenceDatasets.length ?? 0;
              },
            },
          ] as SidebarTabConfig<ProposalGenerationSidebarContext>[])
        : []),
      ...(context.stages.length > 1
        ? ([
            {
              id: "stages",
              label: "Stages",
              icon: TimelineOutlined,
              renderContent: ({
                context,
              }: {
                context?: ProposalGenerationSidebarContext;
              }) => {
                if (!context) return null;
                return (
                  <StagesTab
                    stages={context.stages}
                    currentDialogueId={context.currentDialogueId}
                    onStageClick={context.onStageClick}
                    onClearStage={context.onClearStage}
                    onRemoveStage={context.onRemoveStage}
                    onAddStage={context.onAddStage}
                    isMutating={context.isMutating}
                  />
                );
              },
              renderHeaderActions: () => null,
              getBadgeContent: (context?: ProposalGenerationSidebarContext) => {
                return context?.stages.length ?? 0;
              },
            },
          ] as SidebarTabConfig<ProposalGenerationSidebarContext>[])
        : []),
    ],
    defaultTab: "sources",
    responsive: {
      enableMobile: true,
      enableDesktopNav: true,
      enableResize: true,
      minWidth: 320,
      maxWidth: 800,
      defaultWidth: 420,
    },
    autoOpen: {
      enabled: () => preferences?.autoOpenSidebar ?? true,
      autoClose: false, // Don't close sidebar when sources clear between messages
      getContentCounts: (context) => ({
        sources: context?.responseSources.length ?? 0,
        artifacts: context?.artifacts
          ? Object.keys(context.artifacts).length
          : 0,
        report: context?.reportSdrId ? 1 : 0,
        plan: context?.plans.length ?? 0,
      }),
      autoSelectTab: "sources",
      triggerKey: "sources",
    },
  };
};
